import { valibotResolver } from "@hookform/resolvers/valibot";
import { useForm } from "react-hook-form";

import { Button } from "common/components/ui/Button";
import {
  Dialog,
  DialogContent,
  DialogTitle,
  DialogTrigger,
} from "common/components/ui/Dialog";
import {
  Drawer,
  DrawerContent,
  DrawerTitle,
  DrawerTrigger,
} from "common/components/ui/Drawer";
import FormField from "common/components/ui/FormField";
import { Input } from "common/components/ui/Input";
import useTwBreakpoint from "common/hooks/useTwBreakpoint";
import type { EmailOutreachType } from "common/types";

import { INTEGRATIONS_MAP } from "../constants";
import useIntegrationMutations from "../datahooks/useIntegrationMutations";
import { IntegrationApiKeySchema } from "../schema";

export default function EmailOutreachConnect({
  type,
}: {
  type: EmailOutreachType;
}) {
  const isDesktop = useTwBreakpoint("lg");
  const {
    connectEmailOutreachIntegration,
    isConnectingEmailOutreachIntegration,
  } = useIntegrationMutations();

  const {
    register,
    handleSubmit,
    formState: { errors },
    reset,
  } = useForm({
    resolver: valibotResolver(IntegrationApiKeySchema),
    defaultValues: { api_key: "" },
  });

  function onSubmit({ api_key }: { api_key: string }) {
    connectEmailOutreachIntegration({ type, apiKey: api_key });
  }

  const { title } = INTEGRATIONS_MAP[type];

  const Component = isDesktop ? Dialog : Drawer;
  const Trigger = isDesktop ? DialogTrigger : DrawerTrigger;
  const Content = isDesktop ? DialogContent : DrawerContent;
  const Title = isDesktop ? DialogTitle : DrawerTitle;

  return (
    <Component onAfterClose={reset}>
      <Trigger asChild>
        <Button variant="secondary-black" size="sm">
          Connect
        </Button>
      </Trigger>
      <Content>
        <Title className="mb-8">Paste your {title} API key</Title>

        <FormField
          label="API key"
          htmlFor={`${type}-api-key`}
          hint="Keep your API key safe and do not share it with third parties"
          className="mb-8"
          error={errors.api_key?.message}
        >
          <Input
            id={`${type}-api-key`}
            placeholder="Enter your API key here"
            {...register("api_key")}
          />
        </FormField>

        <Button
          onClick={handleSubmit(onSubmit)}
          className="ml-auto"
          isLoading={isConnectingEmailOutreachIntegration}
        >
          Save
        </Button>
      </Content>
    </Component>
  );
}
